if (!customElements.get('story-flow')) {
  class StoryFlow extends HTMLElement {
    constructor() {
      super();

      const desktopSvgEl = this.querySelector('[data-line] svg');
      const mobileSvgEl = this.querySelector('[data-line-mobile] svg');

      this.desktopSVG = {
        svg: desktopSvgEl,
        path: desktopSvgEl.querySelector('path:not([fill])'),
        dot: desktopSvgEl.querySelector('path[fill]'),
      };
      this.mobileSVG = {
        svg: mobileSvgEl,
        path: mobileSvgEl.querySelector('path:not([fill])'),
        dot: mobileSvgEl.querySelector('path[fill]'),
      };
      this.itemsWrapper = this.querySelector('[data-items-wrapper]');
      this.items = Array.from(this.itemsWrapper.children);
      this.state = {
        isMobile: !breakpointTablet.matches,
        lastScrollY: 0,
        isInitializing: true,
        currentProgress: 0,
        currentSVG: null,
        pathToAnimate: null,
        dotPath: null,
        tl: null,
        dotAnimation: null,
        itemStates: new Map(),
        resizeObserver: null,
        mediaQuery: breakpointTablet,
        handleResize: null,
        handleMediaChange: null,
      };
    }

    connectedCallback() {
      window.addEventListener('load', this.init.bind(this));
    }

    init() {
      gsap.registerPlugin(MotionPathPlugin, ScrollTrigger);

      gsap.config({
        nullTargetWarn: false,
        autoSleep: 60,
        force3D: true,
      });

      this.setCurrentSVG();
      this.setupEventListeners();
      this.initializeCardStates();
      this.initAnimations();
    }

    setupEventListeners() {
      this.state.handleResize = debounce(() => {
        const isSameDevice = this.state.isMobile === !breakpointTablet.matches;

        if (!isSameDevice) {
          gsap.registerPlugin(MotionPathPlugin, ScrollTrigger);

          this.state.isMobile = !breakpointTablet.matches;

          this.setCurrentSVG();
          this.initAnimations();
        }

        if (this.state.currentSVG.path) {
          this.calculateCardProgress();
          this.checkCardActivation();
        }
      }, 200);

      window.addEventListener('resize', this.state.handleResize);
    }

    setCurrentSVG() {
      this.state.currentSVG = this.state.isMobile
        ? this.mobileSVG
        : this.desktopSVG;
    }

    calculateCardProgress() {
      if (!this.state.currentSVG.path) return;

      const pathLength = this.state.currentSVG.path.getTotalLength();
      const wrapperRect = this.itemsWrapper.getBoundingClientRect();
      const samples = 50;
      const points = [];
      const viewBox = this.state.currentSVG.svg.viewBox.baseVal;

      const svg = this.state.currentSVG.svg;
      const svgRect = svg.getBoundingClientRect();
      const svgXScale = svgRect.width / (viewBox?.width || svgRect.width);
      const svgYScale = svgRect.height / (viewBox?.height || svgRect.height);

      for (let i = 0; i <= samples; i++) {
        const progress = i / samples;
        const point = this.state.currentSVG.path.getPointAtLength(
          progress * pathLength,
        );

        const containerX =
          point.x * svgXScale + (svgRect.left - wrapperRect.left);
        const containerY =
          point.y * svgYScale + (svgRect.top - wrapperRect.top);

        points.push({
          x: containerX,
          y: containerY,
          progress: progress,
        });
      }

      const itemProgresses = [];
      this.items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const itemX = rect.left + rect.width / 2 - wrapperRect.left;
        const itemY = rect.top + rect.height / 2 - wrapperRect.top;
        let closestDistance = Infinity;
        let bestProgress = 0;
        for (const point of points) {
          const dx = point.x - itemX;
          const dy = point.y - itemY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < closestDistance) {
            closestDistance = distance;
            bestProgress = point.progress;
          }
        }
        itemProgresses.push({ item, progress: bestProgress, index });
      });

      itemProgresses.sort((a, b) => a.progress - b.progress);
      itemProgresses.forEach((itemData, sortedIndex) => {
        if (sortedIndex > 0) {
          const prevProgress = itemProgresses[sortedIndex - 1].progress;
          itemData.progress = Math.max(itemData.progress, prevProgress);
        }
        itemData.item.setAttribute('data-progress', itemData.progress);
      });
    }

    initializeCardStates() {
      this.calculateCardProgress();
      this.state.itemStates.clear();

      this.items.forEach((item, index) => {
        this.state.itemStates.set(index, {
          active: false,
          progress: parseFloat(item.getAttribute('data-progress') || '0'),
        });
      });

      this.state.isInitializing = false;
      this.checkCardActivation();
    }

    checkCardActivation() {
      const currentScrollY = window.scrollY;

      if (!this.state.isInitializing) {
        this.state.lastScrollY = currentScrollY;
      }

      const scrollTrigger =
        ScrollTrigger.getById('pathAnimation') ||
        (this.state.tl ? this.state.tl.scrollTrigger : null);
      this.state.currentProgress = scrollTrigger ? scrollTrigger.progress : 0;

      this.items.forEach((item, index) => {
        const itemState = this.state.itemStates.get(index) || {
          active: false,
          progress: 0,
        };
        const itemProgress = parseFloat(
          item.getAttribute('data-progress') || '0',
        );
        const activateImmediately = item.hasAttribute(
          'data-activate-immediately',
        );
        const activationThreshold = activateImmediately
          ? 0
          : this.state.isMobile
          ? itemProgress - 0.02
          : itemProgress;

        if (this.state.currentProgress >= activationThreshold) {
          if (!itemState.active) {
            item.classList.add('is-active');
            this.state.itemStates.set(index, {
              active: true,
              progress: itemProgress,
            });
          }
        } else if (
          itemState.active &&
          !this.state.isInitializing &&
          !activateImmediately
        ) {
          item.classList.remove('is-active');
          this.state.itemStates.set(index, {
            active: false,
            progress: itemProgress,
          });
        }
      });
    }

    initAnimations() {
      if (this.state.tl) this.state.tl.kill();
      if (this.state.dotAnimation) this.state.dotAnimation.kill();

      const pathLength = this.state.currentSVG.path.getTotalLength();
      gsap.set(this.state.currentSVG.path, {
        strokeDasharray: pathLength,
        strokeDashoffset: pathLength,
        strokeWidth: this.state.isMobile ? 1.2 : 1.5,
        visibility: 'visible',
      });

      this.state.tl = gsap.timeline({
        scrollTrigger: {
          trigger: this,
          start: () => 'top 55%',
          end: 'bottom bottom',
          scrub: 1,
          markers: false,
          id: 'pathAnimation',
        },
      });

      this.state.tl.to(this.state.currentSVG.path, {
        strokeDashoffset: 0,
        ease: 'none',
      });

      this.state.dotAnimation = gsap.to(this.state.currentSVG.dot, {
        motionPath: {
          path: this.state.currentSVG.path,
          align: this.state.currentSVG.path,
          alignOrigin: [0.5, 0.5],
          autoRotate: true,
        },
        scrollTrigger: {
          trigger: this,
          start: () => 'top 55%',
          end: 'bottom bottom',
          scrub: 1,
          markers: false,
          onUpdate: this.checkCardActivation.bind(this),
        },
        ease: 'none',
      });
    }
  }

  customElements.define('story-flow', StoryFlow);
}
